<template>
  <section id="section-betting" class="betting-section">
    <header class="betting-section__top">
      <div class="betting-section__heading">
        <h2>{{ $t('betting.title') }}</h2>
        <p>{{ $t('betting.desc') }}</p>
      </div>
      <form class="betting-filters" @submit.prevent="applyFilters">
        <label class="betting-filters__field">
          <span class="betting-filters__label">{{ $t('betting.startDate') }}</span>
          <input v-model="startDate" type="date" :max="endDate">
        </label>
        <label class="betting-filters__field">
          <span class="betting-filters__label">{{ $t('betting.endDate') }}</span>
          <input v-model="endDate" type="date" :min="startDate" :max="todayDate">
        </label>
        <label class="betting-filters__field betting-filters__field--grow">
          <span class="betting-filters__label">{{ $t('betting.gameName') }}</span>
          <input
            v-model.trim="searchInput"
            type="search"
            :placeholder="$t('betting.gameNamePlaceholder')"
            autocomplete="off"
          >
        </label>
        <button type="submit" class="betting-filters__btn">{{ $t('betting.search') }}</button>
      </form>
    </header>

    <div v-if="summaryVisible" class="betting-summary">
      <div class="betting-summary__item">
        <span>{{ $t('betting.totalBet') }}</span>
        <strong>{{ formatCurrency(totalBetAmount) }}</strong>
      </div>
      <div class="betting-summary__item">
        <span>{{ $t('betting.totalWin') }}</span>
        <strong>{{ formatCurrency(totalWinAmount) }}</strong>
      </div>
      <div class="betting-summary__item">
        <span>{{ $t('betting.profit') }}</span>
        <strong :class="profitClass">{{ formatSignedMoney(profitAmount) }}</strong>
      </div>
    </div>

    <div class="betting-panel">
      <p v-if="loading" class="betting-section__status">{{ $t('betting.loading') }}</p>
      <p v-else-if="loadError" class="betting-section__status betting-section__status--error">{{ loadError }}</p>
      <p v-else-if="!rows.length" class="betting-section__status">{{ $t('betting.empty') }}</p>

      <div v-else class="betting-table-wrap">
        <table class="betting-table">
          <thead>
            <tr>
              <th scope="col">{{ $t('betting.colDateTime') }}</th>
              <th scope="col">{{ $t('betting.colProvider') }}</th>
              <th scope="col">{{ $t('betting.colGameName') }}</th>
              <th scope="col">{{ $t('betting.colCategory') }}</th>
              <th scope="col" class="betting-table__num">{{ $t('betting.colBetAmount') }}</th>
              <th scope="col" class="betting-table__num">{{ $t('betting.colWinAmount') }}</th>
              <th scope="col" class="betting-table__num">{{ $t('betting.colPreBalance') }}</th>
              <th scope="col" class="betting-table__num">{{ $t('betting.colAfterBalance') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in rows" :key="`${row.round}-${row.regdate}-${index}`">
              <td>{{ formatUtcDateTime(row.regdate) }}</td>
              <td>{{ displayValue(row.provider_name) }}</td>
              <td>{{ displayValue(row.game_code) }}</td>
              <td>{{ betHistoryCategoryLabel(row.category) }}</td>
              <td class="betting-table__num">{{ formatCurrency(row.bet_amount) }}</td>
              <td class="betting-table__num betting-table__num--win">{{ formatCurrency(row.win_amount) }}</td>
              <td class="betting-table__num">{{ formatCurrency(row.pre_balance) }}</td>
              <td class="betting-table__num">{{ formatCurrency(row.after_balance) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <footer v-if="total > 0" class="betting-pagination">
      <button
        type="button"
        class="betting-pagination__btn"
        :disabled="page <= 1 || loading"
        @click="goPage(page - 1)"
      >
        {{ $t('common.prev') }}
      </button>
      <span class="betting-pagination__info">{{ page }} / {{ totalPages }}</span>
      <button
        type="button"
        class="betting-pagination__btn"
        :disabled="page >= totalPages || loading"
        @click="goPage(page + 1)"
      >
        {{ $t('common.next') }}
      </button>
    </footer>
  </section>
</template>

<script>
import { AuthService } from '@/services'
import { getLoginId, getMemberId } from '@/utils/auth'
import {
  BET_HISTORY_PAGE_SIZE,
  BET_HISTORY_CATEGORY_ALL,
  buildBetHistoryDateRange,
  formatDateInputValue,
  parseDateInputValue,
  toBetHistoryApiDate,
  normalizeBetHistoryList,
  betHistoryCategoryLabel
} from '@/utils/betHistory'
import { formatUtcDateTime, displayValue } from '@/utils/format'

export default {
  name: 'BlackGoldBettingPage',
  data () {
    const range = buildBetHistoryDateRange(7)
    return {
      loading: false,
      loadError: '',
      rows: [],
      total: 0,
      totalBetAmount: 0,
      totalWinAmount: 0,
      page: 1,
      startDate: formatDateInputValue(range.start),
      endDate: formatDateInputValue(range.end),
      todayDate: formatDateInputValue(new Date()),
      searchInput: '',
      searchQuery: ''
    }
  },
  computed: {
    pageSize () {
      return BET_HISTORY_PAGE_SIZE
    },
    totalPages () {
      return Math.max(1, Math.ceil(this.total / this.pageSize))
    },
    summaryVisible () {
      return !this.loading && !this.loadError && (this.rows.length > 0 || this.total > 0)
    },
    profitAmount () {
      return Number(this.totalWinAmount) - Number(this.totalBetAmount)
    },
    profitClass () {
      if (this.profitAmount > 0) return 'betting-summary__profit--plus'
      if (this.profitAmount < 0) return 'betting-summary__profit--minus'
      return ''
    }
  },
  mounted () {
    this.loadHistory()
  },
  methods: {
    formatUtcDateTime,
    displayValue,
    betHistoryCategoryLabel,
    reload () {
      this.loadHistory()
    },
    applyFilters () {
      this.searchQuery = this.searchInput
      this.page = 1
      this.loadHistory()
    },
    goPage (nextPage) {
      if (nextPage < 1 || nextPage > this.totalPages || nextPage === this.page) return
      this.page = nextPage
      this.loadHistory()
    },
    formatSignedMoney (value) {
      const num = Number(value)
      if (!Number.isFinite(num)) return this.formatCurrency(0)
      const prefix = num > 0 ? '+' : ''
      return `${prefix}${this.formatCurrency(num)}`
    },
    buildRequestPayload () {
      const loginid = getLoginId()
      const start = parseDateInputValue(this.startDate, false)
      const end = parseDateInputValue(this.endDate, true)
      if (!loginid) {
        return { error: this.$t('betting.errorNoLogin') }
      }
      if (!start || !end) {
        return { error: this.$t('betting.errorNoDateRange') }
      }
      if (start.getTime() > end.getTime()) {
        return { error: this.$t('betting.errorDateRange') }
      }
      return {
        payload: {
          loginid,
          member: getMemberId(),
          direct: 0,
          category: BET_HISTORY_CATEGORY_ALL,
          start_time: toBetHistoryApiDate(start),
          end_time: toBetHistoryApiDate(end),
          timezoneOffset: new Date().getTimezoneOffset(),
          search_key: 'game_code',
          search_val: this.searchQuery || '',
          offset: (this.page - 1) * this.pageSize,
          limit: this.pageSize,
          order_key: 'regdate',
          order_dir: 0
        }
      }
    },
    async loadHistory () {
      const built = this.buildRequestPayload()
      if (built.error) {
        this.loadError = built.error
        this.rows = []
        this.total = 0
        return
      }

      this.loading = true
      this.loadError = ''
      try {
        const res = await AuthService.getBetHistory(built.payload)
        const body = res && res.data ? res.data : null
        if (body && body.code === 0 && body.data) {
          this.rows = normalizeBetHistoryList(body.data)
          this.total = Number(body.data.total) || this.rows.length
          this.totalBetAmount = Number(body.data.total_bet_amount) || 0
          this.totalWinAmount = Number(body.data.total_win_amount) || 0
        } else {
          this.rows = []
          this.total = 0
          this.totalBetAmount = 0
          this.totalWinAmount = 0
          this.loadError = (body && body.message) ? body.message : this.$t('betting.loadError')
        }
      } catch (err) {
        this.rows = []
        this.total = 0
        this.totalBetAmount = 0
        this.totalWinAmount = 0
        this.loadError = this.$t('betting.loadError')
      } finally {
        this.loading = false
      }
    }
  }
}
</script>
